import dayjs from 'dayjs';
import {computeAverageOfSummedupCompletionPercentOfKeyResults} from '@utils/proportions';

export interface GoalActivityFeedItem {
  activity?: string;
  keyResult?: string;
  updatedAt?: string;
}

export interface ProgressHistoryActivity {
  currentValue?: number;
  endDate?: string;
  isGoalEndDate?: boolean;
  isTodayDate?: boolean;
  percentageProgress?: number;
  prevValue?: number;
}

interface ProgressMetric {
  activities?: ProgressHistoryActivity[];
  currentValue?: number;
  id?: string;
  startValue?: number;
  targetValue?: number;
}

const numericValue = (value: string) => {
  const normalized = value.replace(/,/g, '').replace(/[^0-9.+-]/g, '');
  if (!normalized) return undefined;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const parseResultValueChange = (description = '') => {
  const match = description.match(
    /\bchanged\b[\s\S]*\bvalue of\b[\s\S]*\bfrom\s+"([^"]+)"\s+\bto\s+"([^"]+)"/i,
  );
  if (!match) return undefined;

  const prevValue = numericValue(match[1]);
  const currentValue = numericValue(match[2]);
  if (prevValue === undefined || currentValue === undefined) return undefined;

  return {prevValue, currentValue};
};

const activityTimestamp = (activity: ProgressHistoryActivity) =>
  dayjs(activity.endDate).valueOf();

const mergeActivities = (activities: ProgressHistoryActivity[]) => {
  const unique = new Map<string, ProgressHistoryActivity>();

  activities.forEach((activity) => {
    if (!activity.endDate || !dayjs(activity.endDate).isValid()) return;
    const key = `${dayjs(activity.endDate).valueOf()}-${
      activity.currentValue ?? activity.percentageProgress ?? ''
    }`;
    unique.set(key, activity);
  });

  return Array.from(unique.values()).sort(
    (a, b) => activityTimestamp(a) - activityTimestamp(b),
  );
};

export const addFeedHistoryToResults = <T extends ProgressMetric>(
  keyResults: T[] = [],
  feed: GoalActivityFeedItem[] = [],
): T[] =>
  keyResults.map((keyResult) => {
    const resultFeed = feed
      .filter(
        (item) =>
          item.keyResult === keyResult.id &&
          item.updatedAt &&
          dayjs(item.updatedAt).isValid(),
      )
      .sort(
        (a, b) => dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
      );
    const valueChanges = resultFeed.flatMap((item) => {
      const change = parseResultValueChange(item.activity);
      return change
        ? [{...change, endDate: item.updatedAt} as ProgressHistoryActivity]
        : [];
    });

    if (!valueChanges.length) return keyResult;

    const firstChange = valueChanges[0];
    const creationRecord = resultFeed.find(
      (item) =>
        !parseResultValueChange(item.activity) &&
        dayjs(item.updatedAt).valueOf() <= activityTimestamp(firstChange),
    );
    const baselineDate = creationRecord?.updatedAt
      ? creationRecord.updatedAt
      : dayjs(firstChange.endDate).subtract(1, 'millisecond').toISOString();
    const baseline: ProgressHistoryActivity = {
      currentValue: firstChange.prevValue,
      endDate: baselineDate,
      prevValue: keyResult.startValue,
    };

    return {
      ...keyResult,
      activities: mergeActivities([
        ...(keyResult.activities || []),
        baseline,
        ...valueChanges,
      ]),
    };
  });

export const buildOverallGoalProgressHistory = (
  keyResults: ProgressMetric[] = [],
): ProgressHistoryActivity[] => {
  if (!keyResults.length) return [];

  const currentValues = new Map(
    keyResults.map((result) => [result.id, result.startValue]),
  );
  const events = keyResults
    .flatMap((result) =>
      (result.activities || []).flatMap((activity, index) => {
        const currentValue = Number(activity.currentValue);
        if (
          activity.isGoalEndDate ||
          activity.isTodayDate ||
          !activity.endDate ||
          !dayjs(activity.endDate).isValid() ||
          !Number.isFinite(currentValue)
        ) {
          return [];
        }

        return [
          {
            currentValue,
            date: activity.endDate,
            index,
            resultId: result.id,
          },
        ];
      }),
    )
    .sort((a, b) => {
      const dateDifference = dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
      return dateDifference || a.index - b.index;
    });

  const history: ProgressHistoryActivity[] = [];
  events.forEach((event) => {
    currentValues.set(event.resultId, event.currentValue);
    const metricsAtEvent = keyResults.map((result) => ({
      ...result,
      currentValue: currentValues.get(result.id),
    }));
    const percentageProgress = computeAverageOfSummedupCompletionPercentOfKeyResults(
      metricsAtEvent,
    );
    const existingAtTimestamp = history.find(
      (activity) =>
        dayjs(activity.endDate).valueOf() === dayjs(event.date).valueOf(),
    );

    if (existingAtTimestamp) {
      existingAtTimestamp.percentageProgress = percentageProgress;
    } else {
      history.push({endDate: event.date, percentageProgress});
    }
  });

  return history;
};
